import { CvvProvider } from "@lib/contexts/cvv";
import debugContext from "@lib/contexts/debug";
import DebugKey from "@lib/debug_key";
import { useEffect, useState } from "react";
import Script from "next/script";
import type { AppProps } from "next/app";
import "../styles/globals.css";
import "semantic-ui-css/components/search.min.css";
import "semantic-ui-css/components/transition.min.css";
import "semantic-ui-css/components/popup.min.css";
import useInterval from "@hudson/ui/lib/use_interval";
import { parentScrollToTheTop } from "@hudson/ui/lib/utils";
import { useRouter } from "next/router";
import chroma from "chroma-js";
import Session from "@lib/session";
import Head from "next/head";
import SystemErrorNotifier from "@components/SystemErrorNotifier";
import MyApolloProvider from "@components/MyApolloProvider";
import { ErrorBoundary } from "@appsignal/react";
import Appsignal from "@appsignal/javascript";
import TopLevelScripts from "@lib/analytics/TopLevelScripts";
import FrameConfig from "@lib/classes/FrameConfig";
import { localeMap } from "@lib/i18n";
import { useAutosizeDocument } from "@hudson/ui/lib/use_autosize";

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [parentScroll, setParentScroll] = useState(0);
  const [debugMode, setDebugMode] = useState(false);
  useAutosizeDocument();

  const appsignal = new Appsignal({
    key: process.env.NEXT_PUBLIC_APPSIGNAL_FRONTEND_KEY,
    revision: process.env.NEXT_PUBLIC_REVISION,
  });
  if (router.pathname === "/_error" || router.pathname === "/404")
    return <Component {...pageProps} />;

  // Analytics
  const config = pageProps.config || {};

  // Setup globals for currency.
  if (global.window) global.window.currency = pageProps.config?.currency || "USD";

  // Disable (and then enable) buttons during page transitions. Otherwise ind. pages
  // will enable a button when they trigger a page transition but during the transition
  // buttons are enabled. Looks weird.
  useEffect(() => {
    const disableButtons = () => {
      document.querySelectorAll("button").forEach((e) => (e.disabled = true));
    };
    const enableButtons = () => {
      document.querySelectorAll("button").forEach((e) => (e.disabled = false));
    };

    console.log(
      `Booking Engine ${process.env.NEXT_PUBLIC_APP_VERSION} expertly developed by the team at Hudson Software, https://hudsonltd.com/`,
    );

    router.events.on("routeChangeStart", disableButtons);
    router.events.on("routeChangeComplete", enableButtons);

    return () => {
      router.events.off("routeChangeStart", disableButtons);
      router.events.off("routeChangeComplete", enableButtons);
    };
  }, []);

  // Default service area.
  useEffect(() => {
    if (!pageProps.config?.defaultServiceArea) return;
    if (!Session.getConfig().serviceArea)
      Session.updateConfig({ serviceArea: pageProps.config.defaultServiceArea });
  }, [pageProps.config]);

  // Scroll parent to top on page change.
  useEffect(() => {
    if (parentScroll == 0) {
      setParentScroll(1);
      return;
    } else parentScrollToTheTop();
  }, [router.pathname]);

  // Check expirations every 20 seconds.
  useInterval(() => {
    if (Session.isExpired(1)) {
      Session.clearSession();
      if (router.pathname.includes("/book"))
        router.replace(`/${router.query.siteId}/book?session_expired=1`);
    }
  }, 20000);

  if (!router.isReady) return null;

  const colors = pageProps.colors || {
    primaryHover: "#fff",
    primaryColor: "#fff",
    primaryText: "#000",
  };

  const apiKey = config.googleApiKey;

  let colorStyles;
  if (colors) {
    colorStyles = (
      <style jsx global>{`
        html {
          height: auto !important;
        }
        * {
          --error-background: ${chroma("red").set("hsl.s", 0.63).set("hsl.l", 0.56).hex()};
          --field-outline: ${chroma(colors.primaryColor).alpha(0.6).hex()};
          --primary-color: ${chroma(colors.primaryColor).hex()};
          --primary-color-darker: ${chroma(colors.primaryColor).darken(0.25).desaturate(1).hex()};
          --primary-color-lighter: ${chroma(colors.primaryColor).alpha(0.1).hex()};
          --primary-color-much-lighter: ${chroma(colors.primaryColor).brighten(0.5).hex()};
          --primary-hover: ${chroma(colors.primaryHover).hex()};
          --primary-text: ${chroma(colors.primaryText).hex()};
          --upfront-button-hover: ${chroma
            .mix(chroma(243, 244, 246), chroma(colors.primaryColor), 0.3)
            .hex()};
          --upfront-input-bg: ${chroma
            .mix(chroma(243, 244, 246), chroma(colors.primaryColor), 0.05)
            .hex()};
          --upfront-input-bg-focus: ${chroma
            .mix(chroma(243, 244, 246), chroma(colors.primaryColor), 0.05)
            .brighten(1)
            .hex()};
          --warning-background: ${chroma("yellow").set("hsl.s", 0.63).set("hsl.l", 0.56).hex()};
        }
      `}</style>
    );
  }

  return (
    <>
      <Head>
        <link rel="icon" href="data:;base64,iVBORw0KGgo=" />
        <meta name="author" content="Hudson Software, https://hudsonltd.com/" />
        <meta name="description" content="Hudson Software's premier reservation system" />
      </Head>
      <ErrorBoundary
        instance={appsignal}
        tags={{ requestId: pageProps.requestId, sessionId: pageProps.sessionId }}
      >
        <debugContext.Provider value={{ debugMode, setDebugMode }}>
          <DebugKey>
            <CvvProvider>
              <SystemErrorNotifier>
                {(showError) => (
                  <MyApolloProvider
                    apolloCache={pageProps.apolloCache}
                    requestId={pageProps.requestId}
                    showError={showError}
                    sessionId={pageProps.sessionId}
                  >
                    {config?.googleApiKey && (
                      <Script
                        src={`https://maps.googleapis.com/maps/api/js?loading=async&libraries=places,geometry&key=${apiKey}&language=${
                          localeMap[new FrameConfig().language || "en"] ||
                          new FrameConfig().language
                        }&callback=Function.prototype`}
                      />
                    )}

                    {(config?.googleAnalyticsId || config?.gtmId) && (
                      <TopLevelScripts
                        googleAnalyticsId={config.googleAnalyticsId}
                        gtmId={config.gtmId}
                      />
                    )}

                    {colorStyles}

                    <div className="max-w-2xl mx-auto normal:max-w-7xl bg-white p-2 xxs:p-4">
                      <Component {...pageProps} />
                    </div>
                  </MyApolloProvider>
                )}
              </SystemErrorNotifier>
            </CvvProvider>
          </DebugKey>
        </debugContext.Provider>
      </ErrorBoundary>
    </>
  );
}

export default App;
